import { useEffect, useState, useContext, useMemo } from "react";
import { Grid, IconButton, InputAdornment, TextField, Button } from "@mui/material";
import styles from "./styles.module.css";
import ItemView from "components/utils/ItemView/ItemView";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as AddCategory } from "images/svg/copy.svg";
import { ReactComponent as AddProductIcon } from "images/svg/3square.svg";
import { ReactComponent as MoreIcon } from "images/svg/menu.svg";
import { ReactComponent as EditIcon } from "images/svg/edit.svg";
import AddItemPopup from "components/popups/AddItemPopup/AddItemPopup";
import ProductContext from "context/ProductContext";
import AddCategoryPopup from "components/popups/AddCategoryPopup/AddCategoryPopup";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DropdownMenu from "components/utils/DropDownMenu/DropDownMenu";
import SyncIcon from "images/svg/refresh-2.svg";
import SearchContext from "context/SearchContext";
import EditItemDetailsPopup from "components/popups/EditItemDetailsPopup/EditItemDetailsPopup";
import EditCategoryPopup from "components/popups/AddCategoryPopup/EditCategoryPopup";

function matchesSearch(item, searchVal) {
  if (!searchVal) return true;
  return (
    item.name?.toLowerCase().includes(searchVal) ||
    String(item.ean || "")
      .toLowerCase()
      .includes(searchVal) ||
    String(item.plu || "")
      .toLowerCase()
      .includes(searchVal)
  );
}

function useFilteredData(items, categories, searchTerm, filterText, selectedCategoryId) {
  const searchVal = (searchTerm.trim() !== "" ? searchTerm : filterText).trim().toLowerCase();

    const filteredItems = useMemo(() => {
    let resultItems = [];
    if (searchVal !== "") {
      resultItems = items.filter((item) => matchesSearch(item, searchVal));
    } else {
      resultItems = items.filter((item) => item.category === selectedCategoryId);
    }
    return resultItems.sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      return orderA - orderB;
    });
  }, [items, searchVal, selectedCategoryId]);
  
  const sortedCategories = useMemo(() => {
    if (!searchVal) return categories;
    const matching = categories.filter((c) => items.some((i) => i.category === c.id && matchesSearch(i, searchVal)));
    const nonMatching = categories.filter((c) => !matching.includes(c));
    return [...matching, ...nonMatching];
  }, [categories, items, searchVal]);

  return { filteredItems, sortedCategories };
}

function createDialogHelpers(updateProduct, setters) {
  return {
    handleItemUpdated: (updated) => {
      updateProduct(updated);
      setters.setSelectedItem(null);
    },
    handleOpenEditCategory: (e, cat) => {
      if (e?.stopPropagation) e.stopPropagation();
      setters.setEditCat({ open: true, category: cat });
    },
    handleCloseEditCategory: () => setters.setEditCat({ open: false, category: null }),
  };
}

function useDialogState(updateProduct) {
  const [openAddItem, setOpenAddItem] = useState(false);
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editCat, setEditCat] = useState({ open: false, category: null });
  const helpers = createDialogHelpers(updateProduct, { setSelectedItem, setEditCat });

  return {
    openAddItem,
    setOpenAddItem,
    openAddCategory,
    setOpenAddCategory,
    selectedItem,
    setSelectedItem,
    editCat,
    ...helpers,
  };
}

function buildCategoryState(prodCtx, searchCtx, filter, category, data, dialogs) {
  return {
    categories: prodCtx.categories,
    items: prodCtx.items,
    isLoading: prodCtx.isLoading,
    getInitialData: prodCtx.getInitialData,
    filterText: filter.text,
    setFilterText: filter.set,
    selectedCategoryId: category.id,
    setSelectedCategoryId: category.set,
    setSearchTerm: searchCtx.setSearchTerm,
    ...data,
    ...dialogs,
  };
}

function useInitialCategory(categories, setSelectedCategoryId) {
  useEffect(() => {
    if (categories?.length) setSelectedCategoryId((prev) => prev ?? categories[0].id);
  }, [categories, setSelectedCategoryId]);
}

function useCategoryManageState() {
  const prod = useContext(ProductContext);
  const search = useContext(SearchContext);
  const [filterText, setFilterText] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(prod.categories[0]?.id || null);

  const dialogs = useDialogState(prod.updateProduct);
  const data = useFilteredData(prod.items, prod.categories, search.searchTerm, filterText, selectedCategoryId);

  useInitialCategory(prod.categories, setSelectedCategoryId);

  const filter = { text: filterText, set: setFilterText };
  const cat = { id: selectedCategoryId, set: setSelectedCategoryId };
  return buildCategoryState(prod, search, filter, cat, data, dialogs);
}

function SearchField({ filterText, setFilterText }) {
  return (
    <TextField
      placeholder="Search"
      size="small"
      className={styles.searchField}
      value={filterText}
      onChange={(event) => setFilterText(event.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="end">
            <IconButton edge="end" style={{ paddingLeft: "0" }}>
              <img src={SearchIcon} alt="Search" className={styles.icon} />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}

function HeaderTitle() {
  return (
    <div>
      <h2>Categories & Products</h2>
      <h4>Manage Products, Pricing, and Inventory</h4>
    </div>
  );
}

function HeaderButtons({ onAddCat, onAddItem, onReload }) {
  const moreBtn = (
    <Button className={styles.addCustBtn}>
      <MoreIcon className={styles.icon} />
      More
    </Button>
  );
  const reloadItem = { icon: SyncIcon, label: "Reload", onClick: onReload };
  return (
    <>
      <Button onClick={onAddCat} className={styles.addCustBtn}>
        <AddCategory className={styles.icon} />
        Create Category
      </Button>
      <Button onClick={onAddItem} className={styles.addCustBtn}>
        <AddProductIcon className={styles.icon} />
        Create Product
      </Button>
      <DropdownMenu button={moreBtn} items={[reloadItem]} />
    </>
  );
}

function ManageHeader({ filterText, setFilterText, onAddCat, onAddItem, onReload }) {
  return (
    <Grid item xs={12} className={styles.headerSection}>
      <HeaderTitle />
      <div className={styles.actionSection}>
        <SearchField filterText={filterText} setFilterText={setFilterText} />
        <div className={styles.bar}> </div>
        <HeaderButtons onAddCat={onAddCat} onAddItem={onAddItem} onReload={onReload} />
      </div>
    </Grid>
  );
}

function CategoryItem({ category, selectedId, onSelect, onEdit }) {
  const isSelected = selectedId === category.id;
  return (
    <Grid
      item
      xs={12}
      className={`${styles.categoryItem} ${isSelected ? styles.activeCategory : ""}`}
      onClick={() => onSelect(category.id)}
    >
      <span className={styles.categoryName}>{category.name}</span>
      <EditIcon
        onClick={(e) => onEdit(e, category)}
        className={`${styles.icon} ${isSelected ? styles.activeIcon : ""}`}
      />
    </Grid>
  );
}

function CategorySidebar({ loading, categories, selectedId, onSelect, onEdit }) {
  if (loading) {
    return <SkeletonLoading isOpen={loading} count={8} height={40} xs={3} md={12} sm={12} lg={12} />;
  }
  return (
    <Grid className={styles.categoryListWrapper} padding={"0"}>
      <Grid container rowGap={1}>
        {categories.map((cat) => (
          <CategoryItem key={cat.id} category={cat} selectedId={selectedId} onSelect={onSelect} onEdit={onEdit} />
        ))}
      </Grid>
    </Grid>
  );
}

function ProductItem({ item, onOpenDetails }) {
  return (
    <div className={styles.itemWrapper}>
      <ItemView item={item} productView={true} onAction={onOpenDetails} />
    </div>
  );
}

function ProductGrid({ loading, items, onOpenDetails }) {
  if (loading) {
    return <SkeletonLoading isOpen={loading} height={96} count={9} lg={4} md={6} sm={12} xs={12} />;
  }
  return (
    <Grid className={styles.itemListWrapper} padding={"0"}>
      <div className={styles.itemsGrid}>
        {items.length > 0 ? (
          items.map((item, idx) => (
            <ProductItem key={item.id ?? item._id ?? idx} item={item} onOpenDetails={onOpenDetails} />
          ))
        ) : (
          <div className={styles.noItemsMessage}>No items found.</div>
        )}
      </div>
    </Grid>
  );
}

function AddPopups({ state }) {
  const closeItem = () => state.setOpenAddItem(false);
  const closeCat = () => state.setOpenAddCategory(false);
  return (
    <>
      <AddItemPopup open={state.openAddItem} onClose={closeItem} categories={state.categories} />
      <AddCategoryPopup open={state.openAddCategory} onClose={closeCat} onCreate={() => {}} />
    </>
  );
}

function EditItemPopup({ selectedItem, onClose, onUpdate }) {
  if (!selectedItem) return null;
  return <EditItemDetailsPopup open={!!selectedItem} item={selectedItem} onClose={onClose} onUpdate={onUpdate} />;
}

function EditCatPopup({ editCat, onClose, onUpdated }) {
  return (
    <EditCategoryPopup open={editCat?.open} onClose={onClose} category={editCat?.category} onUpdated={onUpdated} />
  );
}

function EditPopups({ state }) {
  return (
    <>
      <EditItemPopup
        selectedItem={state.selectedItem}
        onClose={() => state.setSelectedItem(null)}
        onUpdate={state.handleItemUpdated}
      />
      <EditCatPopup
        editCat={state.editCat}
        onClose={state.handleCloseEditCategory}
        onUpdated={() => state.getInitialData()}
      />
    </>
  );
}

function ManagePopups({ state }) {
  return (
    <>
      <AddPopups state={state} />
      <EditPopups state={state} />
    </>
  );
}

function ManageContentArea({ state, onCatSelect }) {
  return (
    <Grid item className={styles.contentArea} xs={12} md>
      <CategorySidebar
        loading={state.isLoading}
        categories={state.sortedCategories}
        selectedId={state.selectedCategoryId}
        onSelect={onCatSelect}
        onEdit={state.handleOpenEditCategory}
      />
      <ProductGrid loading={state.isLoading} items={state.filteredItems} onOpenDetails={state.setSelectedItem} />
    </Grid>
  );
}

function ManageInnerWrapper({ state, onCatSelect }) {
  return (
    <Grid container height="100%" alignContent={"flex-start"}>
      <ManageHeader
        filterText={state.filterText}
        setFilterText={state.setFilterText}
        onAddCat={() => state.setOpenAddCategory(true)}
        onAddItem={() => state.setOpenAddItem(true)}
        onReload={state.getInitialData}
      />
      <ManageContentArea state={state} onCatSelect={onCatSelect} />
    </Grid>
  );
}

function ManagePageContent({ state }) {
  const onCatSelect = (id) => {
    state.setSelectedCategoryId(id);
    state.setSearchTerm("");
    state.setFilterText("");
  };
  return (
    <Grid container style={{ height: "100%", marginTop: 0 }}>
      <Grid item xs={12} className={styles.contentWrapper}>
        <ManageInnerWrapper state={state} onCatSelect={onCatSelect} />
      </Grid>
      <ManagePopups state={state} />
    </Grid>
  );
}

export default function CategoryAndProductManagePage() {
  const state = useCategoryManageState();
  return <ManagePageContent state={state} />;
}
